import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";
import { cn } from "@/lib/utils";

/** State color, as on GitHub: green open/passed, purple landed, red failed, orange/yellow attention, blue info. */
export type Tone = "default" | "green" | "red" | "orange" | "yellow" | "blue" | "purple";

/** Foreground color for an icon or text in a tone. */
export const toneText: Record<Tone, string> = {
  default: "text-muted-foreground",
  green: "text-success",
  red: "text-danger",
  orange: "text-warning",
  yellow: "text-warning",
  blue: "text-info",
  purple: "text-merged",
};

/** Primer's Label (outlined pill) and StateLabel (filled, `variant="state"`). */
const badgeVariants = cva(
  "inline-flex h-5 items-center gap-1 whitespace-nowrap rounded-full border px-[7px] text-xs font-medium leading-[18px] hover:no-underline [&_svg]:shrink-0",
  {
    variants: {
      tone: {
        default: "border-border text-muted-foreground",
        green: "border-success-muted text-success",
        red: "border-danger-muted text-danger",
        orange: "border-severe-muted text-severe",
        yellow: "border-warning-muted text-warning",
        blue: "border-info-muted text-info",
        purple: "border-merged-muted text-merged",
      },
      variant: {
        outline: "",
        state: "h-8 gap-1.5 border-0 px-3 text-sm font-semibold leading-4 text-on-emphasis",
      },
    },
    compoundVariants: [
      { variant: "state", tone: "default", className: "bg-neutral-emphasis text-on-emphasis" },
      { variant: "state", tone: "green", className: "bg-success-emphasis text-on-emphasis" },
      { variant: "state", tone: "red", className: "bg-danger-emphasis text-on-emphasis" },
      { variant: "state", tone: "orange", className: "bg-warning-emphasis text-on-emphasis" },
      { variant: "state", tone: "yellow", className: "bg-warning-emphasis text-on-emphasis" },
      { variant: "state", tone: "blue", className: "bg-info-emphasis text-on-emphasis" },
      { variant: "state", tone: "purple", className: "bg-merged-emphasis text-on-emphasis" },
    ],
    defaultVariants: { tone: "default", variant: "outline" },
  },
);

function Badge({ className, tone, variant, asChild = false, ...props }: React.ComponentProps<"span"> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span";
  return <Comp data-slot="badge" className={cn(badgeVariants({ tone, variant }), className)} {...props} />;
}

/** Primer's CounterLabel: a count beside a tab or heading. */
function Counter({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="counter"
      className={cn("inline-block min-w-5 rounded-full border border-transparent bg-counter px-1.5 py-0.5 text-center text-xs font-semibold leading-none text-foreground", className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants, Counter };
